import { useEffect } from 'react';

import styled from '@emotion/styled';
import { Error } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';

import type { LatestImage } from '../../../../@types/latestImages';
import { annotationStyles } from '../../../../theme/annotationStyles';
import type { Source } from '../../../../utils/cornerstone/types';
import { useCornerstone } from '../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../utils/cornerstone/useDicomCanvas';
import type { ImageObject } from '../../../annotator/types';

const CornerstoneElement = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 16em;
  max-width: 22em;
  position: relative;
  border-radius: 1em;
  overflow: hidden;
  pointer-events: none;
  ${annotationStyles}
  &.smaller-images {
    height: 10em;
    max-width: 14em;
  }
  &.hide-image {
    opacity: 0;
  }
  &.hide-annotations {
    svg {
      opacity: 0;
    }
  }
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
`;

export const SimpleThumbnail = ({
  image,
  hideAnnotations,
  findingTypes,
  hasPagination,
}: {
  image: LatestImage;
  hideAnnotations: boolean;
  findingTypes?: string[];
  hasPagination?: boolean;
}) => {
  const [dicomCanvasRef, setDicomCanvasRef] = useDicomCanvas();

  // set up the cornerstone canvas
  const viewportId = `thumbnails-${image.imageId}-${
    hideAnnotations ? 'no-annotations' : 'annotations'
  }`;
  let displayFindings: Source = 'ins-summary';
  if (
    findingTypes?.includes('radiographic_boneloss') ||
    findingTypes?.includes('radiographic_boneloss_calculus')
  ) {
    displayFindings = 'additional-image';
  }

  const { isCornerstoneReady, loadImageFileIntoCanvas, imageLoadStatus } =
    useCornerstone({
      isThumbnail: false,
      dicomCanvasRef,
      activeImage: image as unknown as ImageObject,
      viewportId,
      source: displayFindings,
    });

  const imageClasses = classNames({
    'image-canvas': true,
    'hide-image': imageLoadStatus === 'loading',
    'hide-annotations': hideAnnotations,
    'smaller-images': !hasPagination,
  });

  useEffect(() => {
    if (isCornerstoneReady && image && dicomCanvasRef) {
      loadImageFileIntoCanvas(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'loadImageFileIntoCanvas'. Either include it or remove the dependency array. */
  }, [image, dicomCanvasRef, isCornerstoneReady]);

  return (
    <>
      <ImageContainer
        className={imageClasses}
        key={`thumb-${image.imageId}`}
        id={`thumb-${image.imageId}`}
      >
        {imageLoadStatus === 'error' && <Error color="error" />}
        <CornerstoneElement
          className="cornerstoneElement"
          ref={setDicomCanvasRef}
          onContextMenu={(e) => e.preventDefault()}
        />
        {imageLoadStatus === 'loading' && (
          <LoadingSpinner>
            <CircularProgress />
          </LoadingSpinner>
        )}
      </ImageContainer>
    </>
  );
};
